import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

/**
 * useUserNameMap — id → display name ("First Last", falling back to email) for
 * every user in the org, from GET /api/users-list. Powers "Last updated by
 * {name}" resolution (users → contacts, same source the dashboard actor lookup
 * uses). Shares the ['users-list'] query key + 5-minute staleTime with
 * UserManagement, so react-query dedupes the fetch.
 */

interface UserRow {
  id: string;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}

export function useUserNameMap(): Record<string, string> {
  const { data } = useQuery<UserRow[]>({
    // Names for everyone who ever touched a record, so a departed user still resolves.
    queryKey: ['users-list', 'all'],
    queryFn: ({ signal }) => api.get<UserRow[]>('/api/users-list?include_inactive=1', signal),
    staleTime: 5 * 60 * 1000,
  });

  return useMemo(() => {
    const map: Record<string, string> = {};
    for (const u of data ?? []) {
      map[u.id] = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || u.id;
    }
    return map;
  }, [data]);
}
